import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { PwaUpdateService } from './_services/pwa-update.service';
import { PwaSessionService } from './_services/pwa-session.service';
import { Subscription, interval } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Location } from '@angular/common';
import { App } from '@capacitor/app';
import { PayrocDeviceService } from './_services/payroc-device.service';
import { PosUiScaleService } from './_services/pos-ui-scale.service';
import { PosMenuTextSizeService } from './_services/pos-menu-text-size.service';
import { PosSizeService } from './_services/pos-size.service';
import { environment } from '../environments/environment';
import * as LogRocket from 'logrocket';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'order-api-business';
  private sessionSubscription: Subscription;
  private backButtonListener: any;

  constructor(
    public router: Router,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private updateService: PwaUpdateService,
    private sessionService: PwaSessionService,
    private location: Location,
    private payrocDeviceService: PayrocDeviceService,
    private posUiScaleService: PosUiScaleService,
    private posMenuTextSizeService: PosMenuTextSizeService,
    private posSizeService: PosSizeService,
  ) {
    this.matIconRegistry.addSvgIcon(
      'campaign',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/megaphone.svg')
    );

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const ga = (window as any).ga;
        if (typeof ga === 'function') {
          ga('set', 'page', event.urlAfterRedirects);
          ga('send', 'pageview');
        }
      }
    });
  }

  ngOnInit() {
    this.updateService.init();
    this.posUiScaleService.init();
    this.posMenuTextSizeService.init();
    this.posSizeService.init();

    const isNative = Capacitor.isNativePlatform();

    // Periodically refresh the session
    if (!isNative && ('serviceWorker' in navigator)) {
      this.sessionSubscription = interval(15 * 60 * 1000).subscribe(() => {
        this.sessionService.refreshSession();
      });
    }

    this.initLogRocket();

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isNative) {
      this.payrocDeviceService.startService();
    }

    if ((isStandalone && isMobile) || isNative) {
      document.body.classList.add('standalone-mobile');
    }

    // 🔥 HANDLE ANDROID HARDWARE BACK BUTTON (only on native)
    if (isNative) {
      this.backButtonListener = App.addListener('backButton', () => {
        const currentUrl = this.router.url.split('?')[0]; // ignore query params

        // Routes where pressing back should EXIT the app
        const exitRoutes = ['/login', '/home', '/home/orders']; // adjust as needed

        if (exitRoutes.includes(currentUrl)) {
          App.exitApp();
        } else {
          // Go back in Angular navigation history
          this.location.back();
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
    }

    if (this.backButtonListener) {
      this.backButtonListener.remove();
    }
  }

  private initLogRocket(): void {
    if (!environment.logRocket?.enabled) return;

    LogRocket.init(environment.logRocket.appId, {
      network: {
        requestSanitizer: (request) => {
          // Strip Authorization headers from logged network requests
          if (request.headers['Authorization']) {
            request.headers['Authorization'] = '***';
          }
          // Redact request bodies on auth endpoints
          if (request.url.includes('/auth/') || request.url.includes('/login')) {
            request.body = null;
          }
          return request;
        },
        responseSanitizer: (response) => {
          // Redact response bodies on auth endpoints to avoid logging tokens
          if (response.url.includes('/auth/') || response.url.includes('/login')) {
            response.body = null;
          }
          return response;
        },
      },
      dom: {
        inputSanitizer: true,
      },
    });
  }
}
